import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { SignInButton, useUser } from '@clerk/clerk-react';
import { useClerkSession } from '../hooks/useClerkSession';
import toast from 'react-hot-toast';
import { PageLoader } from '../components/LoadingSpinner';
import { PageShell } from '../components/ui/PageShell';
import { PageHeader } from '../components/ui/PageHeader';
import { Surface } from '../components/ui/Surface';
import { MultiRosterProvider, useMultiRoster } from '../context/MultiRosterContext';
import { useLeagueSettings } from '../context/LeagueSettingsContext';
import { useSharedPlayerData } from '../hooks/useSharedPlayerData';
import { createSharedLeagueConfig } from '../utils/sharedLeagueConfig';
import {
  getSleeperUser,
  importRosterAndSettingsFromSleeper,
  listUserLeagues,
  readSleeperAccount,
  type SleeperAccountMeta,
  type SleeperLeague,
  type SleeperUser,
} from '../utils/sleeperRosterIntegration';
import { parsePastedRoster } from '../utils/rosterPaste';
import type { LeagueSettings } from '../types/trade';
import type { Player } from '../types/player';

const Settings = () => {
  const leagueConfig = useMemo(() => createSharedLeagueConfig({
    format: 'standard',
    scoringFormat: 'ppr',
    teamCount: 12,
  }), []);
  const { players, isLoading } = useSharedPlayerData({
    leagueConfig,
    scaleToTen: true,
    scoringFormat: 'ppr',
  });

  if (isLoading && players.length === 0) {
    return <PageLoader pageName="Settings" loadingText="Loading..." />;
  }

  return (
    <MultiRosterProvider playersPool={players}>
      <SettingsDesk board={players} boardReady={players.length > 0} />
    </MultiRosterProvider>
  );
};

function SettingsDesk({ board, boardReady }: { board: Player[]; boardReady: boolean }) {
  const { isLoaded, signedIn } = useClerkSession();

  if (!isLoaded) {
    return <PageLoader pageName="Settings" loadingText="Loading..." />;
  }

  if (!signedIn) {
    return (
      <PageShell>
        <Helmet>
          <title>Settings | Fantasy Fiesta</title>
        </Helmet>
        <div className="mx-auto max-w-lg py-8 text-center">
          <h1 className="font-display text-3xl font-semibold text-white sm:text-5xl">Settings</h1>
          <p className="mt-4 text-base text-zinc-400 sm:text-lg">
            Sign in to set league rules, connect Sleeper, and import a roster.
          </p>
          <SignInButton mode="modal">
            <button
              type="button"
              className="mt-8 w-full rounded-full bg-fiesta-yellow px-8 py-3.5 text-base font-semibold text-ink sm:w-auto"
            >
              Sign in
            </button>
          </SignInButton>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Helmet>
        <title>Settings | Fantasy Fiesta</title>
        <meta name="description" content="Set league rules, sync Sleeper, or paste a roster from ESPN, NFL, or Yahoo." />
      </Helmet>
      <PageHeader
        title="Settings"
        subtitle="League rules, Sleeper, and roster imports for this account."
      />
      <div className="mx-auto flex max-w-3xl flex-col gap-5 lg:max-w-5xl">
        <LeagueRulesCard />
        <SleeperCard board={board} boardReady={boardReady} />
        <OtherPlatformsCard board={board} boardReady={boardReady} />
      </div>
    </PageShell>
  );
}

function LeagueRulesCard() {
  const { leagueData, updateSettings, resetToDefault } = useLeagueSettings();
  const [draft, setDraft] = useState<LeagueSettings>(leagueData.settings);

  useEffect(() => {
    setDraft(leagueData.settings);
  }, [leagueData.settings]);

  const setRoster = (key: keyof LeagueSettings['roster'], value: number) => {
    setDraft((prev) => ({
      ...prev,
      roster: { ...prev.roster, [key]: value },
      superflex: key === 'SUPERFLEX' ? value > 0 : prev.superflex,
    }));
  };

  const setScoring = (key: keyof LeagueSettings['scoring'], value: number) => {
    setDraft((prev) => ({
      ...prev,
      scoring: {
        ...prev.scoring,
        [key]: value,
        PPR: key === 'pointsPerReception' ? value > 0 : prev.scoring.PPR,
      },
    }));
  };

  return (
    <Surface id="league-rules" className="scroll-mt-24 p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-white">League rules</h2>
          <p className="mt-1 text-sm text-zinc-400">
            {leagueData.leagueName
              ? `Active for ${leagueData.leagueName}.`
              : 'Used by rankings, trades, and your roster.'}
          </p>
        </div>
        {leagueData.lastUpdated ? (
          <p className="text-xs text-zinc-500">
            Updated {new Date(leagueData.lastUpdated).toLocaleDateString()}
          </p>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <NumberField
          label="Teams"
          value={draft.teamCount}
          min={8}
          max={16}
          onChange={(value) => setDraft((prev) => ({ ...prev, teamCount: value }))}
        />
        <NumberField
          label="PPR"
          value={draft.scoring.pointsPerReception}
          min={0}
          max={2}
          step={0.5}
          onChange={(value) => setScoring('pointsPerReception', value)}
        />
        <NumberField
          label="Pass TD"
          value={draft.scoring.pointsPerPassTD}
          min={1}
          max={8}
          onChange={(value) => setScoring('pointsPerPassTD', value)}
        />
        <NumberField label="QB" value={draft.roster.QB} min={0} max={4} onChange={(value) => setRoster('QB', value)} />
        <NumberField label="RB" value={draft.roster.RB} min={0} max={6} onChange={(value) => setRoster('RB', value)} />
        <NumberField label="WR" value={draft.roster.WR} min={0} max={6} onChange={(value) => setRoster('WR', value)} />
        <NumberField label="TE" value={draft.roster.TE} min={0} max={4} onChange={(value) => setRoster('TE', value)} />
        <NumberField label="Flex" value={draft.roster.FLEX} min={0} max={4} onChange={(value) => setRoster('FLEX', value)} />
        <NumberField
          label="Superflex"
          value={draft.roster.SUPERFLEX || 0}
          min={0}
          max={2}
          onChange={(value) => setRoster('SUPERFLEX', value)}
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={() => {
            updateSettings(draft);
            toast.success('League rules saved');
          }}
          className="w-full rounded-full bg-fiesta-yellow px-5 py-3 text-sm font-semibold text-ink sm:w-auto"
        >
          Save rules
        </button>
        <button
          type="button"
          onClick={() => {
            resetToDefault();
            toast.success('Rules reset to defaults');
          }}
          className="w-full rounded-full border border-white/10 px-5 py-3 text-sm text-zinc-300 hover:border-white/20 sm:w-auto"
        >
          Reset
        </button>
      </div>
    </Surface>
  );
}

function SleeperCard({ board, boardReady }: { board: Player[]; boardReady: boolean }) {
  const { user } = useUser();
  const { activeRoster, createRoster, replaceRosterPlayersWithStarters } = useMultiRoster();
  const { updateFromSleeper } = useLeagueSettings();
  const saved = readSleeperAccount(user?.unsafeMetadata);
  const [username, setUsername] = useState(saved?.username ?? '');
  const [sleeperUser, setSleeperUser] = useState<SleeperUser | null>(
    saved
      ? {
          user_id: saved.userId,
          username: saved.username,
          display_name: saved.displayName,
          avatar: saved.avatar,
        }
      : null
  );
  const [leagues, setLeagues] = useState<SleeperLeague[]>([]);
  const [season, setSeason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!saved?.userId) return;
    let cancelled = false;
    (async () => {
      const listed = await listUserLeagues(saved.userId);
      if (cancelled) return;
      setLeagues(listed.leagues);
      setSeason(listed.season);
    })();
    return () => {
      cancelled = true;
    };
  }, [saved?.userId]);

  const persistAccount = async (account: SleeperAccountMeta | null) => {
    if (!user) return;
    await user.update({
      unsafeMetadata: {
        ...user.unsafeMetadata,
        sleeperAccount: account,
      },
    });
  };

  const findLeagues = async () => {
    if (!username.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const found = await getSleeperUser(username.trim());
      if (!found) {
        setError('No Sleeper user with that name.');
        return;
      }
      setSleeperUser(found);
      const listed = await listUserLeagues(found.user_id);
      setLeagues(listed.leagues);
      setSeason(listed.season);
      if (listed.leagues.length === 0) {
        setError(`No leagues for ${found.display_name} in the current or last Sleeper season.`);
      }
      await persistAccount({
        userId: found.user_id,
        username: found.username,
        displayName: found.display_name,
        avatar: found.avatar,
        lastLeagueId: saved?.lastLeagueId,
        lastLeagueName: saved?.lastLeagueName,
        lastSyncedAt: saved?.lastSyncedAt,
      });
    } catch {
      setError('Could not reach Sleeper. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const importLeague = async (league: SleeperLeague) => {
    if (!sleeperUser) return;
    if (!boardReady) {
      toast.error('The board is still loading.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await importRosterAndSettingsFromSleeper(
        sleeperUser.user_id,
        league.league_id,
        board
      );
      if (!result.success) {
        setError(result.error || 'Could not import that league.');
        return;
      }
      let rosterId = activeRoster?.id;
      if (!rosterId) {
        rosterId = await createRoster(result.leagueName || league.name, result.leagueSettings?.superflex ? 'superflex' : 'standard');
      }
      await replaceRosterPlayersWithStarters(rosterId, result.players, result.starterPlayerIds);
      if (result.leagueSettings) {
        updateFromSleeper(result.leagueSettings, result.leagueName || league.name);
      }
      await persistAccount({
        userId: sleeperUser.user_id,
        username: sleeperUser.username,
        displayName: sleeperUser.display_name,
        avatar: sleeperUser.avatar,
        lastLeagueId: league.league_id,
        lastLeagueName: result.leagueName || league.name,
        lastSyncedAt: new Date().toISOString(),
      });
      toast.success(`Imported ${result.players.length} players from ${result.leagueName || league.name}.`);
    } catch {
      setError('Import failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await persistAccount(null);
    setSleeperUser(null);
    setLeagues([]);
    setUsername('');
    toast.success('Sleeper disconnected');
  };

  return (
    <Surface className="p-4 sm:p-6">
      <div className="mb-4 flex items-start gap-3">
        <img src="/images/sleeper.png" alt="" className="mt-0.5 h-6 w-6 object-contain" />
        <div>
          <h2 className="font-display text-xl font-semibold text-white">Sleeper</h2>
          <p className="mt-1 text-sm text-zinc-400">
            Connect a username, pick a league, and we pull the roster plus scoring.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          type="text"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && findLeagues()}
          placeholder="Sleeper username"
          autoComplete="username"
          className="min-h-11 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 sm:flex-1"
        />
        <button
          type="button"
          onClick={findLeagues}
          disabled={busy || !username.trim()}
          className="min-h-11 w-full rounded-full bg-fiesta-yellow px-5 py-2.5 text-sm font-semibold text-ink disabled:opacity-50 sm:w-auto"
        >
          {busy ? 'Looking...' : sleeperUser ? 'Refresh leagues' : 'Find leagues'}
        </button>
      </div>

      {sleeperUser ? (
        <div className="mt-4 flex flex-col gap-2 rounded-xl bg-black/20 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-zinc-300">
            Connected as {sleeperUser.display_name}
            {season ? ` · ${season}` : ''}
          </p>
          <button type="button" onClick={disconnect} className="text-left text-sm text-zinc-500 hover:text-zinc-300 sm:text-right">
            Disconnect
          </button>
        </div>
      ) : null}

      {saved?.lastLeagueName ? (
        <p className="mt-3 text-xs text-zinc-500">
          Last import {saved.lastLeagueName}
          {saved.lastSyncedAt ? `, ${new Date(saved.lastSyncedAt).toLocaleDateString()}` : ''}.
        </p>
      ) : null}

      {error ? <p className="mt-3 text-sm text-fiesta-pink">{error}</p> : null}

      {leagues.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {leagues.map((league) => (
            <li key={league.league_id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => importLeague(league)}
                className="flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 text-left hover:border-fiesta-yellow/40 disabled:opacity-60"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-white">{league.name}</span>
                  <span className="block text-xs text-zinc-500">
                    {league.total_rosters} teams · {league.status.replace('_', ' ')} · {league.season}
                  </span>
                </span>
                <span className="shrink-0 text-sm text-fiesta-yellow">
                  {saved?.lastLeagueId === league.league_id ? 'Sync again' : 'Import'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Surface>
  );
}

function OtherPlatformsCard({ board, boardReady }: { board: Player[]; boardReady: boolean }) {
  const { activeRoster, createRoster, replaceRosterPlayersWithStarters } = useMultiRoster();
  const [paste, setPaste] = useState('');
  const [unmatched, setUnmatched] = useState<string[]>([]);

  const importPaste = async () => {
    if (!boardReady) {
      toast.error('The board is still loading.');
      return;
    }
    const result = parsePastedRoster(paste, board);
    if (result.players.length === 0) {
      toast.error('No names on this board matched that list.');
      setUnmatched(result.unmatched);
      return;
    }
    let rosterId = activeRoster?.id;
    if (!rosterId) {
      rosterId = await createRoster('Pasted roster', 'standard');
    }
    await replaceRosterPlayersWithStarters(rosterId, result.players);
    setUnmatched(result.unmatched);
    toast.success(`Imported ${result.players.length} players.`);
  };

  return (
    <Surface id="other-imports" className="scroll-mt-24 p-4 sm:p-6">
      <h2 className="font-display text-xl font-semibold text-white">ESPN, NFL, and Yahoo</h2>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">
        Those sites do not let a browser log into a private league. Paste a roster export or one name per line and we will match it to this board.
      </p>
      <textarea
        value={paste}
        onChange={(event) => setPaste(event.target.value)}
        rows={7}
        placeholder={'Ja\'Marr Chase, CIN, WR\nChristian McCaffrey\nJosh Allen'}
        className="mt-4 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-sm text-white placeholder:text-zinc-600"
      />
      <button
        type="button"
        onClick={importPaste}
        disabled={!paste.trim()}
        className="mt-3 min-h-11 w-full rounded-full bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/15 disabled:opacity-50 sm:w-auto"
      >
        Import pasted roster
      </button>
      {unmatched.length > 0 ? (
        <p className="mt-3 text-sm text-zinc-500">
          Could not match {unmatched.slice(0, 6).join(', ')}
          {unmatched.length > 6 ? `, plus ${unmatched.length - 6} more` : ''}.
        </p>
      ) : null}
      <p className="mt-4 text-sm text-zinc-500">
        After an import, check the team on <Link to="/my-roster" className="text-fiesta-yellow hover:underline">My Roster</Link>.
      </p>
    </Surface>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-xl bg-black/20 px-3 py-2.5">
      <span className="text-sm text-zinc-400">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="w-20 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-right text-sm text-white"
      />
    </label>
  );
}

export default Settings;
